import { SuccessfulSignInModal } from './SuccessfulSignInModal.web';
import { useIsDesktop } from '../../../../context/ViewJobScreenDisplay';

import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';

export enum Branding {
    THIRD_PARTY_APPLIES = 'third-party-applies',
}

export interface PassportLoginModalProps {
    isOpen: boolean;
    onExit: () => void;
    fromParameter?: string;
    onAuthSuccess?: (e?: CustomEvent) => void | Promise<void>;
    branding?: Branding;
    contextualSignInPassportURL: string;
}

declare global {
    interface Window {
        _INDEED_GNAV?: {
            loginModal?: {
                open: (params: object) => void;
                close: () => void;
            };
        };
    }
}

export interface UsePassportLoginGateOptions {
    applyLink: string | undefined;
    appShareUrl: string | null | undefined;
    isLoggedIn: boolean;
    contextualSignInPassportURL: string;
    onApply: () => Promise<void>;
    mobtk?: string;
}

export function usePassportLoginGate(options: UsePassportLoginGateOptions): {
    isLoginGateActive: boolean;
    loginGateHref: string | undefined;
    onPressApply: () => Promise<void>;
    modal: ReactNode;
} {
    const { applyLink, appShareUrl, isLoggedIn, contextualSignInPassportURL, onApply, mobtk } =
        options;

    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const hasProcessedUrlParam = useRef(false);
    const isDesktop = useIsDesktop();

    // On desktop, gate logged-out users when we have a Passport sign-in URL.
    // On mobile, keep the existing modal-based gate which relies on appShareUrl.
    const isLoginGateActive =
        !isLoggedIn && (isDesktop ? contextualSignInPassportURL !== '' : appShareUrl != null);

    // Check URL for third-party intercept flow (user returning from passport auth redirect)
    useEffect(() => {
        if (hasProcessedUrlParam.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const fromParam = urlParams.get('from');

        // If user is logged in and came from third-party intercept, show success modal
        if (isLoggedIn && applyLink && fromParam === 'jsfe-3pintercept-mobviewjob') {
            hasProcessedUrlParam.current = true;
            setIsSuccessModalOpen(true);
        }
    }, [isLoggedIn, applyLink]);

    const handleExit = useCallback(() => {
        setIsLoginModalOpen(false);
    }, []);

    const handleSuccessModalClose = useCallback(() => {
        setIsSuccessModalOpen(false);
        // Reload page to update logged-in state in UI post Passport Log In
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('from');
            window.location.href = url.toString();
        }
    }, []);

    const handleAuthSuccess = useCallback(() => {
        setIsLoginModalOpen(false);

        // Set URL param to indicate third-party intercept flow
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.set('from', 'jsfe-3pintercept-mobviewjob');
            window.history.replaceState({}, '', url.toString());
        }

        setIsSuccessModalOpen(true);
    }, []);

    // On desktop, the apply button renders as an anchor pointing at the Passport
    // sign-in URL (target=_blank). The browser handles the new-tab navigation
    // natively, which preserves a11y semantics (right-click, middle-click,
    // screen-reader link announcements). We therefore only need to fire the
    // logging side effects on press.
    const loginGateHref =
        isLoginGateActive && isDesktop && contextualSignInPassportURL
            ? contextualSignInPassportURL
            : undefined;

    const onPressApply = useCallback(async () => {
        if (isLoginGateActive && isDesktop) {
            // Anchor href handles navigation; fire logging in the background.
            void onApply();
            return;
        }

        await onApply();
        if (isLoginGateActive) {
            setIsLoginModalOpen(true);
            return;
        }
    }, [isLoginGateActive, isDesktop, onApply]);

    const modal = useMemo(() => {
        // Desktop uses a new-tab Passport flow; the in-app modal is mobile-only.
        if (isDesktop) {
            return null;
        }
        return (
            <>
                {isLoginGateActive && (
                    <PassportLoginModal
                        isOpen={isLoginModalOpen}
                        onExit={handleExit}
                        onAuthSuccess={handleAuthSuccess}
                        contextualSignInPassportURL={contextualSignInPassportURL}
                    />
                )}
                <SuccessfulSignInModal
                    isOpen={isSuccessModalOpen}
                    onClose={handleSuccessModalClose}
                    thirdPartyUrl={applyLink ?? null}
                    mobtk={mobtk}
                />
            </>
        );
    }, [
        isDesktop,
        isLoginGateActive,
        isLoginModalOpen,
        isSuccessModalOpen,
        handleExit,
        handleAuthSuccess,
        handleSuccessModalClose,
        contextualSignInPassportURL,
        applyLink,
        mobtk,
    ]);

    return {
        isLoginGateActive,
        loginGateHref,
        onPressApply,
        modal,
    };
}

const PassportLoginModal: FC<PassportLoginModalProps> = ({
    isOpen,
    onExit,
    fromParameter = 'viewjob-3pintercept',
    onAuthSuccess,
    branding = Branding.THIRD_PARTY_APPLIES,
    contextualSignInPassportURL,
}) => {
    const closePassportModal = useCallback(() => {
        window._INDEED_GNAV?.loginModal?.close();
    }, []);

    const memoizedGetParams = useMemo(() => {
        const onLoginSuccess = (e: CustomEvent) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            closePassportModal();
            void onAuthSuccess?.(e);
        };

        return {
            onOpenChange: onExit,
            fromParameter,
            onLoginSuccess,
            extraParameters: { branding },
        };
    }, [onExit, fromParameter, branding, onAuthSuccess, closePassportModal]);

    useEffect(() => {
        if (isOpen) {
            if (window._INDEED_GNAV?.loginModal?.open) {
                window._INDEED_GNAV.loginModal.open(memoizedGetParams);
            } else if (contextualSignInPassportURL) {
                globalThis.location.href = contextualSignInPassportURL;
            } else {
                console.error(
                    'PassportLoginModal: No contextualSignInPassportURL available for fallback',
                );
                onExit();
            }
        }
    }, [isOpen, contextualSignInPassportURL, memoizedGetParams, onExit]);

    return null;
};

export default PassportLoginModal;
